import { readGuildLeaderIdFromLaunch } from './guildShareUrl';
import { normalizeMiniAppPage, resolveMiniAppPageFromPath } from '../routes';

/** Read query + hash (some Telegram WebViews put params after #). */
export function readMiniAppLaunchParams(): URLSearchParams {
  try {
    const search = new URLSearchParams(window.location.search || '');
    const hashRaw = (window.location.hash || '').slice(1).replace(/^#/, '');
    if (hashRaw) {
      const fromHash = new URLSearchParams(hashRaw);
      fromHash.forEach((v, k) => {
        if (!search.has(k)) search.set(k, v);
      });
    }
    return search;
  } catch {
    return new URLSearchParams();
  }
}

function readTelegramStartParam(
  normalize: (raw: string | null | undefined) => string | null,
): string | null {
  try {
    const params = readMiniAppLaunchParams();
    const keys = ['startapp', 'start_param', 'tgWebAppStartParam', 'start'];
    for (const key of keys) {
      const hit = normalize(params.get(key));
      if (hit) return hit;
    }
  } catch {}
  try {
    const webApp = window.Telegram?.WebApp as { initDataUnsafe?: { start_param?: string; startParam?: string } } | undefined;
    return (
      normalize(webApp?.initDataUnsafe?.start_param) ||
      normalize(webApp?.initDataUnsafe?.startParam) ||
      null
    );
  } catch {
    return null;
  }
}

export function readCloneTokenFromLaunch(): string | null {
  const normalize = (raw: string | null | undefined): string | null => {
    const value = String(raw || '').trim();
    if (!value) return null;
    if (value.startsWith('basket_') || value.startsWith('basket-')) return null;
    if (value.startsWith('convshare_') || value.startsWith('convshare-')) return null;
    if (value.startsWith('clone_') || value.startsWith('clone-')) {
      return value.replace(/^clone[_-]/, '').trim() || null;
    }
    return /^[a-f0-9]{16,64}$/i.test(value) ? value : null;
  };
  try {
    const params = readMiniAppLaunchParams();
    const fromUrl =
      normalize(params.get('clone')) ||
      normalize(params.get('token')) ||
      normalize(params.get('startapp')) ||
      normalize(params.get('start_param')) ||
      normalize(params.get('tgWebAppStartParam')) ||
      normalize(params.get('start'));
    if (fromUrl) return fromUrl;
  } catch {}
  return readTelegramStartParam(normalize);
}

export function readBasketTokenFromLaunch(): string | null {
  const normalize = (raw: string | null | undefined): string | null => {
    const value = String(raw || '').trim();
    if (!value) return null;
    if (value.startsWith('basket_') || value.startsWith('basket-')) {
      return value.replace(/^basket[_-]/, '').trim() || null;
    }
    return null;
  };
  try {
    const params = readMiniAppLaunchParams();
    const fromUrl =
      normalize(params.get('basket')) ||
      normalize(params.get('startapp')) ||
      normalize(params.get('start_param')) ||
      normalize(params.get('tgWebAppStartParam')) ||
      normalize(params.get('start'));
    if (fromUrl) return fromUrl;
  } catch {}
  return readTelegramStartParam(normalize);
}

/** `startapp=builder_{programId}` — open Strategy Builder and load a program. */
export function readBuilderProgramFromLaunch(): string | null {
  const normalize = (raw: string | null | undefined): string | null => {
    const value = String(raw || '').trim();
    if (!value) return null;
    if (value.startsWith('builder_') || value.startsWith('builder-')) {
      return decodeURIComponent(value.replace(/^builder[_-]/, '').trim()) || null;
    }
    return null;
  };
  try {
    const params = readMiniAppLaunchParams();
    const fromUrl =
      normalize(params.get('startapp')) ||
      normalize(params.get('start_param')) ||
      normalize(params.get('tgWebAppStartParam')) ||
      normalize(params.get('start'));
    if (fromUrl) return fromUrl;
  } catch {}
  return readTelegramStartParam(normalize);
}

/** `startapp=artifice_upgrade` — open Settings → Artifice subscribe (Artifice site). */
export function readArtificeUpgradeFromLaunch(): boolean {
  const normalize = (raw: string | null | undefined): boolean => {
    const value = String(raw || '').trim().toLowerCase().replace(/-/g, '_');
    return value === 'artifice_upgrade' || value === 'artificeupgrade' || value.startsWith('artifice_upgrade');
  };
  try {
    const params = readMiniAppLaunchParams();
    if (
      normalize(params.get('startapp')) ||
      normalize(params.get('start_param')) ||
      normalize(params.get('tgWebAppStartParam')) ||
      normalize(params.get('start'))
    ) {
      return true;
    }
    if (
      String(params.get('page') || '').toLowerCase() === 'settings' &&
      String(params.get('upgrade') || '').toLowerCase() === 'artifice'
    ) {
      return true;
    }
  } catch {}
  return readTelegramStartParam((raw) => (normalize(raw) ? 'artifice_upgrade' : null)) != null;
}

/** `startapp=script_{draftId}` — open Script Lab tab and load a draft. */
export function readScriptDraftFromLaunch(): string | null {
  const normalize = (raw: string | null | undefined): string | null => {
    const value = String(raw || '').trim();
    if (!value) return null;
    if (value.startsWith('script_') || value.startsWith('script-')) {
      return decodeURIComponent(value.replace(/^script[_-]/, '').trim()) || null;
    }
    return null;
  };
  try {
    const params = readMiniAppLaunchParams();
    const fromUrl =
      normalize(params.get('startapp')) ||
      normalize(params.get('start_param')) ||
      normalize(params.get('tgWebAppStartParam')) ||
      normalize(params.get('start'));
    if (fromUrl) return fromUrl;
  } catch {}
  return readTelegramStartParam(normalize);
}

export function readConvShareTokenFromLaunch(): string | null {
  const normalize = (raw: string | null | undefined): string | null => {
    const value = String(raw || '').trim();
    if (!value) return null;
    if (value.startsWith('convshare_') || value.startsWith('convshare-')) {
      return value.replace(/^convshare[_-]/, '').trim() || null;
    }
    return null;
  };
  try {
    const params = readMiniAppLaunchParams();
    const fromUrl =
      normalize(params.get('convshare')) ||
      normalize(params.get('startapp')) ||
      normalize(params.get('start_param')) ||
      normalize(params.get('tgWebAppStartParam')) ||
      normalize(params.get('start'));
    if (fromUrl) return fromUrl;
  } catch {}
  return readTelegramStartParam(normalize);
}

function hasUrlDeepLinkLaunch(): boolean {
  try {
    const params = readMiniAppLaunchParams();
    if (params.get('strategyId')) return true;
    if (params.get('pair')) return true;
    if (params.get('clone') || params.get('token')) return true;
    if (params.get('basket')) return true;
    if (params.get('convshare')) return true;
    if (params.get('leaderId')) return true;

    const page = normalizeMiniAppPage(params.get('page'));
    if (page && page !== 'home') return true;

    const pathPage = resolveMiniAppPageFromPath(window.location.pathname);
    if (pathPage && pathPage !== 'home') return true;
  } catch {}
  return false;
}

/** True when the MiniApp was opened via a share link or other inbound deep link. */
export function hasMiniAppInboundShareLaunch(): boolean {
  if (readCloneTokenFromLaunch()) return true;
  if (readBasketTokenFromLaunch()) return true;
  if (readConvShareTokenFromLaunch()) return true;
  if (readBuilderProgramFromLaunch()) return true;
  if (readArtificeUpgradeFromLaunch()) return true;
  if (readScriptDraftFromLaunch()) return true;
  if (readGuildLeaderIdFromLaunch()) return true;
  return hasUrlDeepLinkLaunch();
}
